import { cn } from "@omni/utils/styles";
import React, { useState } from "react";
import { FieldValues, Path, PathValue, useFormContext } from "react-hook-form";

import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { KeyNames } from "@/utils/enums";
import { createKeyHandler } from "@/utils/keyboard";

import { BaseField } from "../BaseField/BaseField.component";
import { FormValuesExploded } from "../formField.types";
import { StarIcon } from "./visuals/starIcon";

export type RatingFieldChildProps = { index: number; isActive: boolean };

export type RatingFieldProps<T extends FieldValues> = FormValuesExploded<T> & {
    disabled?: boolean;
    min: number;
    max: number;
    children?: (props: RatingFieldChildProps) => React.ReactNode;
};

const RatingField = <T extends FieldValues>({
    children,
    name,
    label,
    microCopy,
    required,
    disabled,
    min,
    max
}: RatingFieldProps<T>) => {
    const { t } = useGranite();
    const { setValue } = useFormContext<T>();
    const [hoveredValue, setHoveredValue] = useState<number | null>(null);

    const rootRef = React.useRef<HTMLDivElement>(null);

    const handleOnMouseLeave = () => {
        setHoveredValue(null);
    };

    const handleOnMouseEnter = (index: number) => () => {
        setHoveredValue(index);
    };

    const isActive = (index: number, selectedValue: number) => {
        return !hoveredValue ? index <= selectedValue : index <= hoveredValue;
    };

    const handleOnClick = (index: number, selectedValue: number) => () => {
        if (selectedValue === index) {
            setHoveredValue(null);
            setValue(name, null as PathValue<T, Path<T>>, {
                shouldValidate: true,
                shouldTouch: true
            });
        } else {
            setValue(name, index as PathValue<T, Path<T>>, {
                shouldValidate: true,
                shouldTouch: true
            });
        }
    };

    const setFocusOnOption = (index: number) => {
        if (rootRef.current) {
            const ratingElements = rootRef.current.querySelectorAll(
                ".rating-field__option"
            );
            if (ratingElements.length > index) {
                (ratingElements[index] as HTMLElement).focus();
            }
        }
    };

    const handleOnKeyDown = (fieldValue: number) =>
        createKeyHandler([
            {
                keys: [KeyNames.ARROW_LEFT],
                handler: () => {
                    if (fieldValue > min) {
                        const newIndex = fieldValue - min;
                        setValue(name, newIndex as PathValue<T, Path<T>>, {
                            shouldValidate: true,
                            shouldTouch: true
                        });
                        setFocusOnOption(newIndex);
                    }
                }
            },
            {
                keys: [KeyNames.ARROW_RIGHT],
                handler: () => {
                    if (fieldValue < max) {
                        const newIndex = fieldValue + min;
                        setValue(name, newIndex as PathValue<T, Path<T>>, {
                            shouldValidate: true,
                            shouldTouch: true
                        });
                        setFocusOnOption(newIndex);
                    }
                }
            }
        ]);

    const ratingCount = () => {
        return max - min + 1;
    };

    return (
        <BaseField<T, Path<T>>
            name={name}
            label={label}
            microCopy={microCopy}
            required={required}
        >
            {({ field, fieldState }) => {
                return (
                    <div
                        className={cn(
                            "rating-field",
                            disabled && "rating-field--disabled"
                        )}
                        onMouseLeave={handleOnMouseLeave}
                        role="radiogroup"
                        ref={rootRef}
                    >
                        <input
                            id={"rating"}
                            className="d-none"
                            tabIndex={-1}
                        />
                        <div
                            className={cn(
                                "d-flex flex-wrap",
                                fieldState.isInvalid && "is-invalid"
                            )}
                        >
                            {[...Array(ratingCount())].map((_, index) => {
                                const isActiveField = isActive(
                                    index + min,
                                    field.value
                                );
                                return (
                                    // biome-ignore lint/a11y/useSemanticElements: <TODO - Use a radio with label for this>
                                    <div
                                        key={index}
                                        role="radio"
                                        tabIndex={index === 0 ? 0 : -1}
                                        className={cn(
                                            "tw:relative tw:h-12 tw:w-12 tw:border-none tw:bg-white tw:cursor-pointer",
                                            "tw:before:content-[''] tw:before:z-0 tw:before:border tw:before:border-[#ffc107]",
                                            "tw:before:bg-transparent tw:before:absolute tw:before:top-[0.2rem] tw:before:left-[0.2rem]",
                                            "tw:before:h-[calc(100%-0.4rem)] tw:before:w-[calc(100%-0.4rem)] tw:before:rounded-sm tw:before:transition-[background-color] tw:before:duration-100 tw:before:ease-in-out",
                                            isActiveField &&
                                                "tw:before:bg-[#ffc107]"
                                        )}
                                        aria-label={t(
                                            "general.rating.star",
                                            index + 1,
                                            t(
                                                `general.rating.star.rating${index + min}`
                                            )
                                        )}
                                        aria-checked={isActiveField}
                                        onMouseEnter={handleOnMouseEnter(
                                            index + min
                                        )}
                                        onClick={handleOnClick(
                                            index + min,
                                            field.value
                                        )}
                                        onKeyDown={handleOnKeyDown(field.value)}
                                    >
                                        {children ? (
                                            children({
                                                index: index + min,
                                                isActive: isActiveField
                                            })
                                        ) : (
                                            <StarIcon
                                                index={index + min}
                                                isActive={isActiveField}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            }}
        </BaseField>
    );
};

export { RatingField };
